import { defineStore } from 'pinia'
import { AttributionCookie } from '@/enums/gtm'
import type { CampaignLabel, ReactivateReactivatableMembershipsResponse } from '@/types/member'

export const useUserStore = defineStore('user', {
  state: () => ({
    // Thin store for user-specific mutations not in auth
  }),
  actions: {
    async saveAvatar({ avatar, userId }: { avatar: string; userId: string }) {
      const mutation = `
        mutation UpdateAuthUser(
          $id: ID!
          $firstName: String
          $lastName: String
          $email: String
          $phoneNumber: String
          $dateOfBirth: String
          $avatar: String
          $isUser: Boolean = false
        ) {
          updateAuthUser(
            input: {
              id: $id
              firstName: $firstName
              lastName: $lastName
              email: $email
              phoneNumber: $phoneNumber
              dateOfBirth: $dateOfBirth
              avatar: $avatar
            }
          ) {
            authUser {
              _id
              id
              fullName
              firstName
              lastName
              email
              createdAt
              updatedAt
              dateOfBirth
              avatar
              user @include(if: $isUser) {
                _id
                id
                firstName
                lastName
                email
                phoneNumber
                country {
                  _id
                  id
                  name
                  canCancelSubscriptionAfterDays
                  expectedDeliveryDateDailyOrderAfterDays
                  canDoReturnRequestAfterDay
                }
                members {
                  edges {
                    node {
                      status
                    }
                  }
                }
                addresses {
                  edges {
                    node {
                      _id
                      id
                      street
                      fullStreet
                      streetType
                      houseNumber
                      annex
                      building
                      city
                      postalCode
                      country {
                        _id
                        id
                        name
                        hostNamePrefix
                        languages {
                          edges {
                            node {
                              id
                              iso
                            }
                          }
                        }
                      }
                    }
                  }
                }
                billingAddress {
                  _id
                  id
                  street
                  fullStreet
                  streetType
                  houseNumber
                  annex
                  building
                  city
                  postalCode
                  country {
                    _id
                    id
                    name
                    hostNamePrefix
                  }
                }
              }
            }
            clientMutationId
          }
        }
      `

      const {
        public: { API_URL }
      } = useRuntimeConfig()

      const { token } = useApolloToken()

      const response = await $fetch<{
        data?: { updateAuthUser?: any }
        errors?: any[]
      }>(`${API_URL}/graphql`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${token.value}`
        },
        body: {
          operationName: 'UpdateAuthUser',
          variables: { id: userId, avatar, isUser: false },
          query: mutation
        }
      })

      if (!response.data || !response.data.updateAuthUser) {
        throw response
      }

      return response.data.updateAuthUser
    },
    async updatePaymentMethod(payload: Record<string, any>) {
      const authStore = useAuthStore()
      const appStore = useAppStore()

      const userId = authStore.authId

      if (!userId) {
        throw new Error('Cannot update payment method: missing authenticated user id')
      }

      const {
        public: { API_URL }
      } = useRuntimeConfig()

      const { token } = useApolloToken()

      const response = await $fetch<any>(`${API_URL}/v2/users/${userId}/verification-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${token.value}`
        },
        body: {
          ...payload,
          language: appStore.locale
        }
      })

      return response
    },
    async sendAttribution({ userId, _hash }: { userId: string | number; _hash?: string }) {
      const {
        public: { API_URL }
      } = useRuntimeConfig()

      const { token } = useApolloToken()
      const otaAttribution = useCookie(AttributionCookie).value as any

      if (!userId || !otaAttribution) return

      const router = useRouter()
      const sourceUrl = router.currentRoute.value.fullPath

      await $fetch(`${API_URL}/v2/users/${userId}/attribution${_hash ? `?_hash=${_hash}` : ''}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(!_hash && token.value ? { Authorization: `Bearer ${token.value}` } : {})
        },
        body: {
          attribution: {
            ...otaAttribution,
            sourceUrl
          }
        }
      })
    },
    async reactivateReactivatableMemberships({
      userId,
      _hash,
      campaignId,
      memberIds
    }: {
      userId: string
      _hash?: string
      campaignId: number
      memberIds: number[]
    }): Promise<ReactivateReactivatableMembershipsResponse> {
      const {
        public: { API_URL }
      } = useRuntimeConfig()

      const { token } = useApolloToken()

      await this.sendAttribution({ userId, _hash })

      const response = await $fetch<ReactivateReactivatableMembershipsResponse>(
        `${API_URL}/v2/users/${userId}/members/reactivateable${_hash ? `?_hash=${_hash}` : ''}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Bearer ${token.value}`
          },
          body: {
            campaignId,
            memberIds
          }
        }
      )

      return response
    },
    async deleteSession() {
      const {
        public: { API_URL }
      } = useRuntimeConfig()
      const { token } = useApolloToken()

      const response = await $fetch(`${API_URL}/v2/session`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${token.value}`
        },
        credentials: 'include' // To accept (and persist) cookies returned form the server
      })

      return response
    },
    async getReactivatableMemberships({ userId, _hash, campaignLabel }: { userId: string; _hash?: string; campaignLabel?: CampaignLabel }) {
      const {
        public: { API_URL }
      } = useRuntimeConfig()

      const { token } = useApolloToken()

      const url = new URL(`${API_URL}/v2/users/${userId}/members/reactivateable`)
      if (_hash) url.searchParams.set('_hash', _hash)
      if (campaignLabel) url.searchParams.set('campaignLabel', campaignLabel)

      const response = await $fetch(url.href, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(!_hash && token.value ? { Authorization: `Bearer ${token.value}` } : {})
        }
      })

      return response
    },
    async getPaymentContract({ userId, _hash }: { userId: string; _hash?: string }) {
      const {
        public: { API_URL }
      } = useRuntimeConfig()

      const { token } = useApolloToken()

      const response = await $fetch(`${API_URL}/v2/users/${userId}/payment-contract${_hash ? `?_hash=${_hash}` : ''}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(!_hash && token.value ? { Authorization: `Bearer ${token.value}` } : {})
        }
      })

      return response
    },
    async anonymiseUser({ userId, promotionalEmailsEnabled }: { userId: number; promotionalEmailsEnabled: boolean }) {
      const {
        public: { API_URL }
      } = useRuntimeConfig()

      const { token } = useApolloToken()

      const response = await $fetch(`${API_URL}/v2/users/${userId}/retain-account`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${token.value}`
        },
        body: {
          promotionalEmailsEnabled
        }
      })

      return response
    }
  }
})
